import { AnimatePresence, motion, useInView } from 'framer-motion';
import { Menu } from 'lucide-react';
import { useRef } from 'react';
import { navMenuItems } from '../../constants/settings';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import GetStartedButton from './Buttons/GetStartedButton';
import NavButton from './Buttons/NavButton';

const Header = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { amount: 1 });

  const navbarVariants = {
    initial: { y: '-80px', opacity: 0 },
    animate: {
      y: '0px',
      opacity: 1,
      transition: { duration: 0.3, ease: 'easeOut' },
    },
    exit: {
      y: '-80px',
      opacity: 0,
      transition: { duration: 0.2, ease: 'easeIn' },
    },
  };

  return (
    <>
      <div ref={ref} />
      <AnimatePresence initial={false}>
        {isInView ? (
          // Transparent Navbar
          <header className='fixed top-0 h-20 z-40 flex w-full items-center justify-between border-b border-gray-600 bg-transparent font-playfair'>
            <nav className='mx-auto flex w-[95%] max-w-[1800px] items-center justify-between xl:w-4/5'>
              <a
                href='#home'
                className='hover:opacity-70 active:scale-95 duration-300'
              >
                <h1 className='text-3xl 2xl:text-4xl font-semibold italic text-legal-brown'>
                  My
                  <span className='font-marcellus not-italic text-legal-white'>
                    Legal
                  </span>
                  ites
                </h1>
              </a>

              <ul className='hidden items-center justify-center gap-5 font-semibold lg:flex lg:text-base xl:text-lg'>
                {navMenuItems.map((link, index) => (
                  <NavButton
                    key={index}
                    href={link.href}
                    btnText={link.label}
                    isScrolled={false}
                  />
                ))}
              </ul>

              <div className='hidden items-center justify-center gap-5 lg:flex'>
                <GetStartedButton isScrolled={false} />
              </div>

              <div className='lg:hidden'>
                <Sheet>
                  <SheetTrigger className='p-2'>
                    <Menu className='size-6 text-legal-white' />
                  </SheetTrigger>
                  <SheetContent side='right' className='w-[300px] bg-gray-900'>
                    <SheetHeader>
                      <SheetTitle className='text-legal-white'>Menu</SheetTitle>
                      <div className='mt-4 w-full border-b border-gray-600' />
                    </SheetHeader>
                    <nav className='mt-6 flex flex-col gap-4'>
                      {navMenuItems.map((link, index) => (
                        <SheetClose key={index}>
                          <a
                            href={link.href}
                            className='text-lg font-semibold text-legal-white transition-colors hover:text-legal-brown'
                            aria-label='Close'
                          >
                            {link.label}
                          </a>
                        </SheetClose>
                      ))}
                      <div className='mt-4'>
                        <GetStartedButton />
                      </div>
                    </nav>
                  </SheetContent>
                </Sheet>
              </div>
            </nav>
          </header>
        ) : (
          // White Background Navbar
          <motion.header
            key='solid'
            initial='initial'
            animate='animate'
            exit='exit'
            variants={navbarVariants}
            className='fixed top-0 z-50 h-20 flex w-full items-center justify-between border-b border-gray-200 bg-white font-playfair shadow-legal-sm'
          >
            <nav className='mx-auto flex w-[95%] max-w-[1800px] items-center justify-between xl:w-4/5'>
              <a
                href='#home'
                className='hover:opacity-70 active:scale-95 duration-300'
              >
                <h1 className='text-3xl 2xl:text-4xl font-semibold italic text-legal-brown'>
                  My
                  <span className='font-marcellus not-italic text-legal-dark-blue'>
                    Legal
                  </span>
                  ites
                </h1>
              </a>

              <ul className='hidden items-center justify-center gap-5 font-semibold lg:flex lg:text-base xl:text-lg'>
                {navMenuItems.map((link, index) => (
                  <NavButton
                    key={index}
                    href={link.href}
                    btnText={link.label}
                    isScrolled={true}
                  />
                ))}
              </ul>

              <div className='hidden items-center justify-center gap-5 lg:flex'>
                <GetStartedButton isScrolled={true} />
              </div>

              <div className='lg:hidden'>
                <Sheet>
                  <SheetTrigger className='p-2'>
                    <Menu className='size-6 text-legal-dark-blue' />
                  </SheetTrigger>
                  <SheetContent side='right' className='w-[300px] bg-gray-900'>
                    <SheetHeader>
                      <SheetTitle className='text-legal-white'>Menu</SheetTitle>
                      <div className='mt-4 w-full border-b border-gray-600' />
                    </SheetHeader>
                    <nav className='mt-6 flex flex-col gap-4'>
                      {navMenuItems.map((link, index) => (
                        <SheetClose key={index} asChild>
                          <a
                            href={link.href}
                            className='text-lg font-semibold text-legal-white transition-colors hover:text-legal-brown'
                          >
                            {link.label}
                          </a>
                        </SheetClose>
                      ))}
                      <div className='mt-4'>
                        <GetStartedButton />
                      </div>
                    </nav>
                  </SheetContent>
                </Sheet>
              </div>
            </nav>
          </motion.header>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
